import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Button,
  Stack,
  Typography,
  Divider,
  Avatar,
  Autocomplete,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import DatasetRoundedIcon from "@mui/icons-material/DatasetRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import { SpecTab } from "./MainTabs/Spec/Spec";
// Commented out — building allCombinationTable in PartLibTableCheckBoxes is
// expensive (nested spec/component/size loops with awaited DB lookups) and
// isn't needed for Estimation, which reads partLibTable directly from
// PipingContext independent of this tab being mounted.
// import { PartLibTableCheckBoxes } from "./MainTabs/PartLib/PartLibTableCheckBoxes";
import { EstimationTab } from "./MainTabs/EstimationTab/EstimationTab";
import NavTabs from "../../../../components/Tabs/NavTabs";
import { usePiping, PipingProvider } from "./PipingContext";
import { SpecHeader } from "./MainTabs/Spec/SpecHeader";
import { useParams, useSearchParams } from "react-router-dom";
import CompareRevisionsButton from "./CompareRevisionsButton";
import LoadProjectDataComboBox from "./LoadProjectDataComboBox";
import { getPlantById } from "./api";
import useEmsTreeAvailable from "../../../../hooks/useEmsTreeAvailable";
import { pipingWbsTabs } from "../../../../constants/projectTypes";

const NEW_REVISION_OPTION = "__new";

const compactComboSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 20,
    height: 25,
    padding: "0 32px 0 8px !important",
    fontSize: 12,
  },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
    padding: "0 !important",
    height: 25,
    lineHeight: "25px",
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
    transform: "translate(14px, 5px) scale(1)",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.75)",
  },
  "& .MuiAutocomplete-endAdornment": {
    top: "50%",
    transform: "translateY(-50%)",
  },
};

function HeaderField({ label, children, minWidth = 160 }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth }}>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

function ProjectSelectionHeader({
  plantId,
  clientName,
  type,
  selectedProject,
  onProjectSelect,
  compareBtnRef,
  isSelectionEditable,
  showRevision = true,
}) {
  const [plantName, setPlantName] = React.useState("");
  // With the EMS tree on screen it is the single place a WBS/revision gets
  // picked, so the header only reports the selection - on every tab.
  const treeOwnsSelection = useEmsTreeAvailable();
  const canEditSelection = isSelectionEditable && !treeOwnsSelection;
  const {
    savePipingData,
    estimationRevisions,
    confirmedEstimationRevision,
    selectEstimationRevision,
    addEstimationRevision,
    modelingData,
    setModelingData,
    selectedModelingRevision,
    selectedEstimationRevision,
  } = usePiping();

  // Only show a revision as selected once it has been confirmed for the
  // project currently in the WBS box — never a leftover from a previous one.
  const displayedRevision =
    confirmedEstimationRevision &&
    confirmedEstimationRevision.projectId === selectedProject?.id
      ? confirmedEstimationRevision.revision
      : null;

  const handleRevisionChange = (_event, value) => {
    if (!value || !selectedProject?.id) return;

    if (value === NEW_REVISION_OPTION) {
      addEstimationRevision();
      return;
    }

    selectEstimationRevision(value);
  };

  const [saving, setSaving] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    severity: "success",
    message: "",
  });

  React.useEffect(() => {
    if (!plantId) return;

    let cancelled = false;

    getPlantById(plantId)
      .then((plant) => {
        if (!cancelled) setPlantName(plant?.name || "");
      })
      .catch((error) => {
        console.log("Failed to load plant", error);
      });

    return () => {
      cancelled = true;
    };
  }, [plantId]);

  const handleSave = async () => {
    setSaving(true);

    try {
      await savePipingData();
      setSnackbar({ open: true, severity: "success", message: "Saved" });
    } catch (error) {
      console.error("Failed to save piping data", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: error?.message || "Failed to save",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        px: 1.5,
        py: 0.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <FolderOpenRoundedIcon fontSize="small" />
        </Stack>

        <HeaderField label="Project UID">
          <Typography variant="body2" fontWeight={600} noWrap>
            {plantName || "—"}
          </Typography>
        </HeaderField>

        <HeaderField label="WBS UID" minWidth={260}>
          {canEditSelection ? (
            <LoadProjectDataComboBox
              plantId={plantId}
              clientName={clientName}
              type={type}
              onSelect={onProjectSelect}
            />
          ) : (
            <Typography variant="body2" fontWeight={600} noWrap>
              {selectedProject?.name || selectedProject?.link || "—"}
            </Typography>
          )}
        </HeaderField>

        {/* Estimation's picker. A Spec WBS has no revisions of its own -
            selectedSpecs is one flat list on the project - so it is hidden
            there rather than left showing a permanent "—". */}
        {showRevision && (
          <HeaderField label="Rev." minWidth={110}>
            {canEditSelection ? (
              <Autocomplete
                size="small"
                fullWidth
                disabled={!selectedProject}
                options={[...estimationRevisions, NEW_REVISION_OPTION]}
                value={displayedRevision}
                getOptionLabel={(option) =>
                  option == null
                    ? ""
                    : option === NEW_REVISION_OPTION
                      ? "+ Add New Revision"
                      : String(option)
                }
                onChange={handleRevisionChange}
                sx={compactComboSx}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select revision" />
                )}
              />
            ) : (
              <Typography variant="body2" fontWeight={600} noWrap>
                {displayedRevision ?? "—"}
              </Typography>
            )}
          </HeaderField>
        )}

        <Box sx={{ alignSelf: "center", flexShrink: 0 }}>
          <CompareRevisionsButton
            ref={compareBtnRef}
            modelingData={modelingData}
            setModelingData={setModelingData}
            revKey={selectedModelingRevision}
          />
        </Box>

        <Button
          className="header-save-btn"
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={saving}
          sx={{ alignSelf: "center", flexShrink: 0 }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default function PnIForm() {
  const {
    modelingData,
    setModelingData,
    selectedModelingRevision,
    selectedEstimationRevision,
    setSelectedProjectId,
    loadedProjectId,
    selectEstimationRevision,
    addEstimationRevision,
  } = usePiping();

  const [currentTab, setCurrentTab] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);

  const { plantId } = useParams();
  const [searchParams] = useSearchParams();

  // Deep link from the EMS tree ("Rev N" nodes, and "+ Add revision", under a
  // Piping WBS): /plant/:plantId/estimation?projectId=&revision=&projectName=
  // (plus newRevision=1 to branch a fresh revision) opens straight into that
  // project the same way the header dropdowns used to.
  const [pendingDeepLink, setPendingDeepLink] = useState(null);
  const appliedDeepLinkRef = useRef(null);

  useEffect(() => {
    const deepLinkProjectId = searchParams.get("projectId");
    if (!deepLinkProjectId) return;

    const revision = searchParams.get("revision");
    const wantsNew = searchParams.get("newRevision") === "1";
    // Keyed on the whole intent, not just the project: picking Rev 2 and then
    // Rev 3 on the same WBS are two different links to act on.
    const key = `${deepLinkProjectId}|${revision}|${wantsNew}`;
    if (appliedDeepLinkRef.current === key) return;
    appliedDeepLinkRef.current = key;

    setSelectedProject({
      id: deepLinkProjectId,
      name: searchParams.get("projectName") || "",
    });
    // The context loads on selectedProjectId - calling loadPipingData here as
    // well would race two loads against each other. Re-picking the project
    // already loaded is a no-op, which is what makes switching revision
    // within it instant.
    setSelectedProjectId(deepLinkProjectId);
    setPendingDeepLink({ projectId: deepLinkProjectId, revision, wantsNew });
  }, [searchParams, setSelectedProjectId]);

  // Applied only once the fetch has landed: parsePipingData clears the
  // revision selection, so anything picked earlier would be wiped, and the
  // branch point for a new revision wouldn't be known yet.
  useEffect(() => {
    if (!pendingDeepLink) return;
    if (String(loadedProjectId) !== String(pendingDeepLink.projectId)) return;

    setPendingDeepLink(null);

    if (pendingDeepLink.wantsNew) {
      addEstimationRevision();
    } else if (pendingDeepLink.revision != null) {
      selectEstimationRevision(pendingDeepLink.revision);
    }
  }, [
    pendingDeepLink,
    loadedProjectId,
    addEstimationRevision,
    selectEstimationRevision,
  ]);

  const clientName = "DEMO";
  const type = "pip";

  // Spec and Estimation are separate WBS now, and the tree names which one it
  // opened in the link. Each is its own record; an Estimation WBS pulls its
  // specs from the plant's Spec record as it loads (see PipingContext).
  //
  // No usable type in the link means the page was reached without the tree,
  // where there is no WBS to have a type: that is the legacy two-tab page, so
  // fall back to it rather than to an empty tab strip.
  const requestedTabs = pipingWbsTabs(searchParams.get("wbsType"));
  const visibleTabs = requestedTabs.length
    ? requestedTabs
    : ["spec", "estimation"];
  const showSpec = visibleTabs.includes("spec");
  const showEstimation = visibleTabs.includes("estimation");

  const CompareBtnRef = useRef(null);

  // Nothing anywhere in the module renders until a revision is confirmed in
  // the header — the picker itself lives above the tabs, so it stays reachable.
  const hasRevision = Boolean(selectedEstimationRevision);

  const gate = (panel) =>
    hasRevision ? (
      panel
    ) : (
      <Box sx={{ p: 4, width: "100%", textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Select a WBS UID and a revision above to load this project.
        </Typography>
      </Box>
    );

  const mainTabs = [];

  if (showSpec) {
    mainTabs.push({
      key: "spec",
      label: "Spec",
      // The revision gate is Estimation's: selectedSpecs is one flat list on
      // the project, not keyed by revision. A Spec WBS has no revision to
      // pick, so gating it would leave the page stuck on "select a revision"
      // for good. The legacy two-tab page keeps the gate it has always had.
      panel: showEstimation ? gate(<SpecTab />) : <SpecTab />,
    });
  }

  // { key: "specLib", label: "Spec Library", panel: <PartLibTableCheckBoxes revision={0} /> },

  if (showEstimation) {
    mainTabs.push({
      key: "estimation",
      label: "Estimation",
      panel: gate(<EstimationTab />),
    });
  }

  // Which tab index means what now depends on the WBS, so read the tab out
  // rather than comparing currentTab to a fixed 0.
  const currentTabKey = mainTabs[currentTab]?.key;

  const handleChange = (newValue) => {
    console.log("Tab changed to:", newValue);
    setCurrentTab(newValue);
  };

  const handleProjectSelect = (project) => {
    console.log("selected project", project);
    setSelectedProject(project);
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        p: 0,
      }}
    >
      <ProjectSelectionHeader
        plantId={plantId}
        clientName={clientName}
        type={type}
        selectedProject={selectedProject}
        onProjectSelect={handleProjectSelect}
        compareBtnRef={CompareBtnRef}
        isSelectionEditable={currentTabKey === "spec"}
        showRevision={showEstimation}
      />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <NavTabs
          tabs={mainTabs}
          navLabel="data-tab"
          handleOutputChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {currentTabKey === "spec" && (hasRevision || !showEstimation) && (
            <Box sx={{ flexGrow: 1 }}>
              <SpecHeader />
            </Box>
          )}

        </NavTabs>
      </Paper>
    </Box>
  );
}

export function Piping() {
  return (
    <PipingProvider>
      <PnIForm />
    </PipingProvider>
  );
}